import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

// Halo buttons. "primary" is the electric-azure signature (azure fill, dark ink);
// "danger" is the hot-pink LIVE heat. Variant names mirror the admin kit so the
// two SPAs stay structurally identical.
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium outline-none transition-all duration-150 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[.98] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-primary font-semibold text-primary-foreground shadow-[0_6px_22px_-8px_hsl(var(--primary)/0.75)] hover:brightness-110 hover:shadow-[0_10px_28px_-8px_hsl(var(--primary)/0.9)]",
        secondary:
          "border border-white/10 bg-secondary text-secondary-foreground hover:border-white/20 hover:bg-accent hover:text-accent-foreground",
        ghost: "text-foreground/75 hover:bg-accent hover:text-foreground",
        danger:
          "bg-destructive-solid font-semibold text-destructive-foreground shadow-[0_6px_22px_-8px_hsl(var(--destructive)/0.7)] hover:brightness-110",
        outline:
          "border border-white/15 bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-8 rounded-md px-3 text-xs",
        default: "h-9 px-4 py-2",
        lg: "h-10 rounded-md px-6",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: { variant: "secondary", size: "default" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, loading, children, disabled, ...rest },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      disabled={disabled || loading}
      className={cn(buttonVariants({ variant, size }), className)}
      {...rest}
    >
      {asChild ? (
        // Slot requires exactly one React element child — pass it through untouched.
        children
      ) : (
        <>
          {loading && <Loader2 className="animate-spin" />}
          {children}
        </>
      )}
    </Comp>
  );
});

export { buttonVariants };
